/**
 * Glossary for HelpTip tooltips and the glossary page (spec §14.6.6). Definitions shared with
 * the rest of the system come from `@mfa-aff/shared` copy/fa.ts (GLOSSARY_FA); the few UI-only
 * terms (balance buckets, attribution, Sheba) are defined here.
 */
import { GLOSSARY_FA } from "@mfa-aff/shared";

export type GlossaryTerm =
    | "activeService"
    | "renewalRate"
    | "codePool"
    | "guarantee"
    | "pending"
    | "available"
    | "paidOut"
    | "referralCode"
    | "partnerPromo"
    | "level"
    | "attribution"
    | "clawback"
    | "sheba"
    | "payout"
    | "wallet"
    | "strategic"
    | "ambassador"
    | "binding";

type Entry = { term: string; definition: string };

const fromShared = (term: string): Entry => {
    const found = GLOSSARY_FA.find((e) => e.term === term);
    if (!found) throw new Error(`Missing shared glossary term: ${term}`);
    return { term: found.term, definition: found.definition };
};

export const GLOSSARY: Record<GlossaryTerm, Entry> = {
    activeService: fromShared("سرویس فعال"),
    renewalRate: fromShared("نرخ تمدید"),
    codePool: fromShared("سهم کد"),
    guarantee: fromShared("دورهٔ ضمانت"),
    referralCode: fromShared("کد معرف"),
    partnerPromo: fromShared("کد تخفیف بازاریاب"),
    level: fromShared("سطح"),
    payout: fromShared("تسویه"),
    wallet: fromShared("کیف پول"),
    strategic: fromShared("شریک راهبردی"),
    ambassador: fromShared("سفیر"),
    pending: {
        term: "در انتظار",
        definition: "پورسانتی که ثبت شده ولی هنوز دورهٔ ضمانتش تمام نشده است. پس از آن خودکار قابل برداشت می‌شود.",
    },
    available: {
        term: "قابل برداشت",
        definition: "مبلغی که قطعی شده و همین حالا می‌توانید به کیف پول یا حساب بانکی‌تان منتقل کنید.",
    },
    paidOut: {
        term: "پرداخت‌شده",
        definition: "جمع مبلغی که تا امروز به کیف پول میزبان‌فا یا حساب بانکی شما واریز شده است.",
    },
    attribution: {
        term: "قاعدهٔ برنده",
        definition: "قاعده‌ای که مشخص می‌کند هر سفارش به نام کدام بازاریاب ثبت شود: کد واردشده، لینک بازشده یا اتصال قبلی مشتری.",
    },
    clawback: {
        term: "بازپس‌گیری",
        definition: "اگر مشتری بعد از قطعی شدن پورسانت وجهش را پس بگیرد، به همان نسبت از پورسانت‌های بعدی شما کم می‌شود. هرگز از شما پول نقد خواسته نمی‌شود.",
    },
    sheba: {
        term: "شبا",
        definition: "شمارهٔ ۲۴ رقمی حساب بانکی که با IR شروع می‌شود. حساب باید به نام خودتان باشد.",
    },
    binding: {
        term: "اتصال مشتری",
        definition: "وقتی مشتری با لینک یا کد شما خرید کند، تا مدتی (پیش‌فرض ۱۲ ماه) به شما متصل می‌ماند و خریدهای بعدی‌اش بدون کد هم به نام شما ثبت می‌شود.",
    },
};

/** All glossary entries sorted alphabetically (Persian collation) for the glossary page. */
export const GLOSSARY_LIST: (Entry & { key: GlossaryTerm })[] = (Object.keys(GLOSSARY) as GlossaryTerm[])
    .map((key) => ({ key, ...GLOSSARY[key] }))
    .sort((a, b) => a.term.localeCompare(b.term, "fa"));
